import { LDPlugin, useLDFlag, useLDClient } from 'launchdarkly-vue-client-sdk';
import type { LDInspection, LDEvaluationDetail } from 'launchdarkly-js-client-sdk';
import useLDContext from '../composables/useLDContext';

const FS_FEATURE_FLAGS = [
    'CRO_1030_MULTI_TABS_FORM',
    'CRO_1111_OPENED_DATE_SELECTOR',
    'CRO_1112_SEO_LANDING_PAGE_CHANGES',
];

export default defineNuxtPlugin((nuxtApp) => {
    const clientSideID = useRuntimeConfig().public.LAUNCH_DARKLY_CLIENT_KEY;
    const context = useLDContext();
    try {
        let useLDFlagImplementation;
        let useLDClientImlementation;
        if (clientSideID && clientSideID !== 'XXXXXX') {
            // FullStory integration
            const evaluatedSessionFlags: { [path: string]: { [flagKey: string]: unknown } } = {};
            const fsInspector: LDInspection = {
                type: 'flag-used',
                name: 'fullstory-inspector',
                method: (key: string, detail: LDEvaluationDetail) => {
                    // FullStory attaches getCurrentSessionURL and setVars to the window object.
                    // https://help.fullstory.com/hc/en-us/articles/1500004101581-FS-setVars-API-Sending-custom-page-data-to-FullStory
                    if (window.FS === undefined || typeof window.FS.getCurrentSessionURL !== 'function') {
                        return;
                    }

                    const url = window.FS.getCurrentSessionURL(true);
                    if (url === undefined || !FS_FEATURE_FLAGS.includes(key)) {
                        return;
                    }

                    evaluatedSessionFlags[url] = {
                        ...evaluatedSessionFlags[url],
                        [key]: detail.value,
                    };
                    const arrayOfFlags = Object.keys(evaluatedSessionFlags[url]).map(
                        (flagKey) => `${flagKey}=${JSON.stringify(evaluatedSessionFlags[url][flagKey], null, 1)}`
                    );
                    if (typeof window.FS.setVars === 'function') {
                        window.FS.setVars('page', { ldflags: arrayOfFlags }, { integration: 'launchdarkly' });
                    }
                },
            };

            nuxtApp.vueApp.use(LDPlugin, { clientSideID, options: { inspectors: [fsInspector] }, user: context });
            useLDFlagImplementation = useLDFlag;
            useLDClientImlementation = useLDClient;
        } else {
            useLDFlagImplementation = (flagKey, defaultValue = false) => ref(defaultValue);
            useLDClientImlementation = () => ({
                track: () => {},
                flush: () => new Promise((resolve) => resolve('')),
                waitForInitialization: () => new Promise((resolve) => resolve('init')),
                identify: () => new Promise((resolve) => resolve({})),
                variationDetail: (flag) => ({ reason: { inExperiment: false } }),
                variation: (flag, defaultValue) => defaultValue,
            });
        }

        nuxtApp.provide('useLDFlag', useLDFlagImplementation);
        nuxtApp.provide('useLDClient', useLDClientImlementation);
    } catch (err) {
        console.error('Error initializing LaunchDarkly plugin:', err);
    }
});
